import { get } from "lodash";
import { ROUTES } from "../constants/routes.constants";

export const ACCESS_LEVELS = {
  FULL: "FULL",
  BILLING_ONLY: "BILLING_ONLY",
  NO_ACCESS: "NO_ACCESS",
};

export const SUBSCRIPTION_STATUS = {
  NONE: "NONE",
  TRIAL: "TRIAL",
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  PAUSED: "PAUSED",
  CANCELED: "CANCELED",
};

// New backend onboarding_step values → route mapping
export const ONBOARDING_STEPS = {
  PHONE_VERIFICATION: "PHONE_VERIFICATION",
  PERSONAL_INFO: "PERSONAL_INFO",
  BUSINESS_INFO: "BUSINESS_INFO",
  SUBSCRIPTION: "SUBSCRIPTION",
};

/**
 * Maps backend onboarding_step to frontend route.
 * Returns null if onboarding is complete (use can_access_player next).
 */
export function getRouteFromOnboardingStep(onboardingStep) {
  switch (onboardingStep) {
    case ONBOARDING_STEPS.PHONE_VERIFICATION:
    case ONBOARDING_STEPS.PERSONAL_INFO:
      return ROUTES.PERSONAL_INFO;
    case ONBOARDING_STEPS.BUSINESS_INFO:
      return ROUTES.BUSINESS_INFO;
    case ONBOARDING_STEPS.SUBSCRIPTION:
      return ROUTES.PACKAGES;
    default:
      return null; // onboarding complete
  }
}

/**
 * Maps backend response flags to auth state.
 */
export function getAuthStateFromResponse(responseData) {
  const onboardingStep = get(responseData, "onboarding_step", null);
  let canAccessPlayer = get(responseData, "can_access_player", undefined);
  const hasActiveSubscription = get(responseData, "has_active_subscription", false);
  const user = get(responseData, "user", {});
  const subscriptionStatus = get(user, "subscription_status", null);

  // If can_access_player is not explicitly set, infer from subscription_status
  if (canAccessPlayer === undefined) {
    canAccessPlayer = subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE || 
                      subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL;
  }

  // Determine auth flags based on onboarding step
  let isPhoneVerified = true;
  let isBusinessVerified = true;
  let isPrimium = canAccessPlayer;

  if (onboardingStep === ONBOARDING_STEPS.PHONE_VERIFICATION ||
      onboardingStep === ONBOARDING_STEPS.PERSONAL_INFO) {
    isPhoneVerified = false;
    isBusinessVerified = false;
    isPrimium = false;
  } else if (onboardingStep === ONBOARDING_STEPS.BUSINESS_INFO) {
    isPhoneVerified = true;
    isBusinessVerified = false;
    isPrimium = false;
  } else if (onboardingStep === ONBOARDING_STEPS.SUBSCRIPTION) {
    isPhoneVerified = true;
    isBusinessVerified = true;
    isPrimium = false;
  } else if (onboardingStep === null || onboardingStep === undefined) {
    // Onboarding complete (or not using onboarding_step field)
    // Infer from subscription status
    isPhoneVerified = true;
    isBusinessVerified = true;
    isPrimium = canAccessPlayer;
  }

  return {
    isAuthenticated: true,
    isPhoneVerified,
    isBusinessVerified,
    isPrimium,
    // Extra info for routing/UI
    onboardingStep,
    canAccessPlayer,
    hasActiveSubscription,
    subscriptionStatus,
  };
}

/**
 * Determines where to redirect after login based on new backend response.
 */
export function getPostLoginRoute(responseData) {
  const onboardingStep = get(responseData, "onboarding_step", null);
  const canAccessPlayer = get(responseData, "can_access_player", false);
  const user = get(responseData, "user", {});
  const status = get(user, "status", "");

  // Check if user is blocked
  if (status === "BLOCKED") {
    return { route: null, blocked: true };
  }

  // Check onboarding step
  const onboardingRoute = getRouteFromOnboardingStep(onboardingStep);
  if (onboardingRoute) {
    return { route: onboardingRoute, blocked: false };
  }

  // Onboarding complete - check player access
  if (canAccessPlayer) {
    return { route: ROUTES.HOME, blocked: false };
  }

  // Can't access player (PAST_DUE, PAUSED, CANCELED) → billing
  const subscriptionStatus = get(user, "subscription_status", null);
  if (subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE) {
    return { route: ROUTES.PROFILE, blocked: false, openTab: "plans" };
  }
  if (subscriptionStatus === SUBSCRIPTION_STATUS.PAUSED) {
    return { route: ROUTES.PLANS, blocked: false };
  }
  // CANCELED users had a subscription before → treat like existing user, go to Profile
  if (subscriptionStatus === SUBSCRIPTION_STATUS.CANCELED) {
    return { route: ROUTES.PROFILE, blocked: false, openTab: "plans" };
  }

  // NONE (first-time buyer) → packages
  return { route: ROUTES.PACKAGES, blocked: false };
}

export function getAccessLevelFromBillingStatus(billingStatus) {
  const status = get(billingStatus, "subscription_status", null);
  return getAccessLevelFromSubscriptionStatus(status);
}

export function getAccessLevelFromSubscriptionStatus(status) {
  if (status === SUBSCRIPTION_STATUS.ACTIVE || status === SUBSCRIPTION_STATUS.TRIAL) {
    return ACCESS_LEVELS.FULL;
  }

  // Business decision: PAST_DUE blocks player but allows billing pages
  if (status === SUBSCRIPTION_STATUS.PAST_DUE) {
    return ACCESS_LEVELS.BILLING_ONLY;
  }

  // Recommended: PAUSED is "needs action" (billing only)
  if (status === SUBSCRIPTION_STATUS.PAUSED) {
    return ACCESS_LEVELS.BILLING_ONLY;
  }

  // CANCELED users had a subscription before → billing only (treat like existing user)
  if (status === SUBSCRIPTION_STATUS.CANCELED) {
    return ACCESS_LEVELS.BILLING_ONLY;
  }

  // NONE (first-time buyer) → no access
  if (status === SUBSCRIPTION_STATUS.NONE) {
    return ACCESS_LEVELS.NO_ACCESS;
  }

  // Default safe behavior: billing only
  return ACCESS_LEVELS.BILLING_ONLY;
}

export function isPaymentRoute(pathname) {
  return pathname === ROUTES.PAYMENT || pathname === ROUTES.PAYMENT_PROCESSING;
}

export function isPlayerRoute(pathname) {
  return (
    pathname === ROUTES.HOME ||
    pathname === ROUTES.MY_SCHEDULE ||
    pathname === ROUTES.MY_PLAYLIST ||
    pathname.startsWith("/playlist/") ||
    pathname === ROUTES.NEW_PLAYLIST ||
    pathname === ROUTES.LIKED ||
    pathname === ROUTES.ARTIST ||
    pathname.startsWith("/artist/")
  );
}

export function getRedirectForBlockedRoute({ accessLevel, billingStatus }) {
  const status = get(billingStatus, "subscription_status", null);

  if (accessLevel === ACCESS_LEVELS.BILLING_ONLY) {
    // PAST_DUE, PAUSED, or CANCELED should land in billing UI
    if (status === SUBSCRIPTION_STATUS.PAST_DUE) return ROUTES.PROFILE;
    if (status === SUBSCRIPTION_STATUS.PAUSED) return ROUTES.PLANS;
    if (status === SUBSCRIPTION_STATUS.CANCELED) return ROUTES.PROFILE;
    return ROUTES.PROFILE;
  }

  if (accessLevel === ACCESS_LEVELS.NO_ACCESS) {
    // NONE (first-time buyer) → packages
    return ROUTES.PACKAGES;
  }

  return ROUTES.HOME;
}

